import { endOfMonth, endOfWeek, format, isValid, parseISO, startOfMonth, startOfWeek, subWeeks } from 'date-fns';
import { NewCasesGroupBy, NewCasesParams } from 'types/matters';

export type QuickPeriod = 'this-week' | 'last-week' | 'this-month';

export const QUICK_PERIODS: readonly QuickPeriod[] = ['this-week', 'last-week', 'this-month'];

/** The value of the Office control's "All" button; every other button's value is an office id. */
export const ALL_OFFICES = 'all';

/** The NEW tab's controls. Dates are `yyyy-MM-dd`; no office id means every office. */
export interface NewCasesFilters {
    groupBy: NewCasesGroupBy;
    officeIds: number[];
    from: string;
    until: string;
}

const MONDAY_FIRST = { weekStartsOn: 1 } as const;
const ISO_DATE = 'yyyy-MM-dd';

/** The fixed span a quick choice stands for on `today`: Monday to Sunday, or the first to the last of the month. */
export function quickPeriodSpan(period: QuickPeriod, today: Date): { from: string; until: string } {
    const lastWeek = subWeeks(today, 1);
    const [from, until] =
        period === 'this-month'
            ? [startOfMonth(today), endOfMonth(today)]
            : period === 'last-week'
              ? [startOfWeek(lastWeek, MONDAY_FIRST), endOfWeek(lastWeek, MONDAY_FIRST)]
              : [startOfWeek(today, MONDAY_FIRST), endOfWeek(today, MONDAY_FIRST)];
    return { from: format(from, ISO_DATE), until: format(until, ISO_DATE) };
}

/** The quick choice whose span the dates are exactly, so any other pair of dates highlights none. */
export function matchingQuickPeriod(from: string, until: string, today: Date): QuickPeriod | undefined {
    return QUICK_PERIODS.find((period) => {
        const span = quickPeriodSpan(period, today);
        return span.from === from && span.until === until;
    });
}

/**
 * The offices after the Office buttons were pressed: pressing All clears every office, pressing an office
 * clears All, and releasing the last office falls back to All.
 */
export function nextOfficeSelection(pressed: string[], selected: number[]): number[] {
    if (pressed.includes(ALL_OFFICES) && selected.length > 0) {
        return [];
    }
    return pressed.filter((value) => value !== ALL_OFFICES).map(Number);
}

function isIsoDate(value: string | null): value is string {
    return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value));
}

/**
 * The controls a URL carries, defaults for what it leaves out or garbles: Practice groups, every office, this
 * week. A quick choice is kept as its name, so it is recomputed for `today`.
 */
export function readNewCasesFilters(params: URLSearchParams, today: Date): NewCasesFilters {
    const groupBy =
        params.get('groupBy') === NewCasesGroupBy.SECTOR_GROUP
            ? NewCasesGroupBy.SECTOR_GROUP
            : NewCasesGroupBy.PRACTICE_GROUP;
    const officeIds = (params.get('offices') ?? '')
        .split(',')
        .map(Number)
        .filter((id) => Number.isInteger(id) && id > 0);
    const period = QUICK_PERIODS.find((quick) => quick === params.get('period'));
    const from = params.get('from');
    const until = params.get('until');
    const span =
        !period && isIsoDate(from) && isIsoDate(until) && from <= until
            ? { from, until }
            : quickPeriodSpan(period ?? 'this-week', today);
    return { groupBy, officeIds, ...span };
}

/** The URL parameters for {@link readNewCasesFilters}, a period matching a quick choice written as its name. */
export function writeNewCasesFilters(filters: NewCasesFilters, today: Date): Record<string, string> {
    const period = matchingQuickPeriod(filters.from, filters.until, today);
    return {
        groupBy: filters.groupBy,
        ...(filters.officeIds.length > 0 ? { offices: filters.officeIds.join(',') } : {}),
        ...(period ? { period } : { from: filters.from, until: filters.until }),
    };
}

/** The request both NEW-tab reads send. */
export function newCasesParams(filters: NewCasesFilters): NewCasesParams {
    return {
        groupBy: filters.groupBy,
        officeIds: filters.officeIds.join(','),
        from: filters.from,
        until: filters.until,
    };
}
